/**
 * Registry of the bottom sheets that are currently open, so a surface that
 * takes over the whole popup can dismiss them instead of trying to out-stack
 * them.
 *
 * Why this exists rather than "just give the takeover a bigger z-index":
 * react-native-web renders every `View` with `position: relative; z-index: 0`,
 * so each one is its own stacking context. Bottom sheets escape that by
 * rendering through `<Portal hostName="global">`, which is a *sibling* of the
 * whole app tree — so an open sheet always paints above anything rendered
 * inside a screen, no matter what z-index that thing asks for. Raising the
 * takeover into the portal instead only moves the problem: the sheets the
 * takeover itself opens (sign warnings, Ledger/QR/hardware-wallet modals, every
 * `Select` menu) would then be the ones stuck underneath.
 *
 * Closing beats stacking here: the takeover replaces the screen the sheet was
 * opened from, so the sheet has nothing left to belong to.
 *
 * See `styles.ts` for the layer numbers this deliberately avoids touching.
 */

type CloseFn = () => void

const openSheets = new Map<string, CloseFn>()

export const registerOpenBottomSheet = (id: string, close: CloseFn) => {
  openSheets.set(id, close)
}

export const unregisterOpenBottomSheet = (id: string) => {
  openSheets.delete(id)
}

/**
 * Ids of the sheets open right now. Read during render by a takeover so it
 * captures the sheets that predate it, *before* its own children mount and
 * register theirs — child effects run before the parent's, so a list gathered
 * in an effect would already include the takeover's own modals.
 */
export const snapshotOpenBottomSheets = (): string[] => [...openSheets.keys()]

/**
 * Closes the given sheets. Call from an effect, never during render: closing
 * drives state inside each BottomSheet. Ids that already closed are skipped, so
 * this is safe to call late or twice.
 */
export const closeBottomSheets = (ids: string[]) => {
  ids.forEach((id) => {
    const close = openSheets.get(id)
    if (!close) return
    try {
      close()
    } catch {
      // A sheet whose Modalize ref is already gone must not stop the rest from
      // closing — the takeover would render on top of whatever is left.
    }
  })
}
